import { calculateCalories, calculateUnits, Drinks } from "./Drinks";
import { BoroughTerminology, GetBoroughByName, GetReferralOptionsForRegion, GetRegionsForCurrentBorough, GetSexualityOptions } from "./Boroughs";
import Boroughs from "./borough-data";
import { RiskLevel } from "@/model/RiskLevel";
import { UserInteractionEvent } from "@/model/UserEvents";
export var QuestionType;
(function (QuestionType) {
    QuestionType[QuestionType["Select"] = 0] = "Select";
    QuestionType[QuestionType["Multichoice"] = 1] = "Multichoice";
    QuestionType[QuestionType["Input"] = 2] = "Input";
    QuestionType[QuestionType["BinaryGender"] = 3] = "BinaryGender";
    QuestionType[QuestionType["UnitCalculator"] = 4] = "UnitCalculator";
    QuestionType[QuestionType["UnitGuide"] = 5] = "UnitGuide";
    QuestionType[QuestionType["AgreeToTerms"] = 6] = "AgreeToTerms";
})(QuestionType || (QuestionType = {}));
export var LayoutStyle;
(function (LayoutStyle) {
    LayoutStyle[LayoutStyle["Grid"] = 0] = "Grid";
    LayoutStyle[LayoutStyle["SingleColumn"] = 1] = "SingleColumn";
})(LayoutStyle || (LayoutStyle = {}));
export var ColumnGrouping;
(function (ColumnGrouping) {
    ColumnGrouping[ColumnGrouping["Left"] = 0] = "Left";
    ColumnGrouping[ColumnGrouping["Right"] = 1] = "Right";
})(ColumnGrouping || (ColumnGrouping = {}));
export const RiskTypeNameForScore = (score) => {
    switch (RiskLevelForScore(score)) {
        case RiskLevel.Lower:
            return "lower";
        case RiskLevel.Increasing:
            return "increasing";
        case RiskLevel.Higher:
            return "higher";
        case RiskLevel.High:
            return "high";
    }
};
export const RiskTextForScore = (score) => {
    const name = RiskTypeNameForScore(score);
    const suffix = name === "high" ? " / Possible Dependence" : "";
    return `${name[0].toUpperCase() + name.slice(1)} Risk${suffix}`;
};
export const RiskLevelForScore = (score) => {
    if (score < 8) {
        return RiskLevel.Lower;
    }
    else if (score < 16) {
        return RiskLevel.Increasing;
    }
    else if (score < 20) {
        return RiskLevel.Higher;
    }
    else {
        return RiskLevel.High;
    }
};
export var Gender;
(function (Gender) {
    Gender[Gender["Male"] = 0] = "Male";
    Gender[Gender["Female"] = 1] = "Female";
})(Gender || (Gender = {}));
export const DrinksState = {
    selectedDrinks: [],
    score: 0,
    allDrinks: {
        kind: QuestionType.Select,
        selected: null,
        required: true,
        label: "Select drink",
        singleColumn: true,
        layoutStyle: LayoutStyle.SingleColumn,
        options: Drinks.map(drink => ({
            label: drink.name,
            image: `${drink.className}`
        }))
    },
    currentServings: {
        kind: QuestionType.Select,
        selected: null,
        required: true,
        layoutStyle: LayoutStyle.SingleColumn,
        label: "Select size",
        singleColumn: true,
        options: []
    }
};
const UnitCalculatorQuestion = {
    question: "<strong>How many units of alcohol do you drink on a typical day when you are drinking?</strong>" +
        " Use the unit calculator below.",
    highlightInvalid: false,
    continuePrompt: {
        label: "Are you sure that&apos;s all you drink on a typical drinking day?",
        confirmText: "<strong>Yes,</strong><br>I&apos;m sure",
        cancelText: "<strong>No,</strong><br>I&apos;ll add more"
    },
    numberedInputs: true,
    inputs: [
        {
            kind: QuestionType.UnitCalculator,
            selected: null,
            required: true,
            options: [DrinksState]
        }
    ]
};
export const isNorthEastAndNorthCumbria = () => window.location.href.toLowerCase().includes("north-east-and-north-cumbria");
const iLiveInOptions = [
    ...Boroughs.filter((borough) => borough.excludeFromNavigation === undefined ||
        !borough.excludeFromNavigation).map((borough) => ({
        label: borough.regionDescription || borough.displayName || borough.name,
        value: borough.displayName || borough.name,
    })),
    {
        label: "Norfolk",
        value: "Norfolk"
    }
].sort((a, b) => (a.label < b.label ? -1 : 1));
const Question1 = {
    question: "Tell us a bit about yourself, so we can tailor the test to you.",
    highlightInvalid: false,
    inputs: [
        {
            kind: QuestionType.Select,
            label: "I live in...",
            required: !isNorthEastAndNorthCumbria(),
            selected: null,
            userEvent: UserInteractionEvent.SelectRegion,
            hide: isNorthEastAndNorthCumbria(),
            options: iLiveInOptions
        },
        {
            kind: QuestionType.BinaryGender,
            required: true,
            selected: null,
            userEvent: UserInteractionEvent.SelectGender,
            options: [
                {
                    label: "Male",
                    image: "male.svg"
                },
                {
                    label: "Female",
                    image: "female.svg"
                }
            ]
        }
    ]
};
export const GetSelectedBorough = () => {
    if (isNorthEastAndNorthCumbria()) {
        return "NorthEastAndNorthCumbria";
    }
    const q1 = Question1.inputs[0].selected;
    return q1 && (q1.value || q1.label);
};
export const GetSelectedGender = () => {
    const q1 = Question1.inputs[1].selected;
    return q1 && q1.label;
};
const PROD_STATE = {
    testCompleted: false,
    debugOverride: false,
    navRequested: () => "",
    region: () => GetBoroughByName(GetSelectedBorough()),
    score: (steps) => {
        // Calculate score
        return steps.reduce((acc, val) => {
            const s = val.inputs
                .map((input) => {
                const selected = input.selected;
                if (input.kind === QuestionType.UnitCalculator) {
                    return input.options[0].score;
                }
                if (selected === null || !selected.hasOwnProperty("score")) {
                    return 0;
                }
                return selected.score || 0;
            })
                .reduce((a, v) => a + v);
            return acc + s;
        }, 0);
    },
    calories: () => calculateCalories(DrinksState.selectedDrinks),
    units: () => calculateUnits(DrinksState.selectedDrinks),
    gender: () => {
        switch (GetSelectedGender()) {
            case "Male":
                return Gender.Male;
            case "Female":
                return Gender.Female;
        }
        return Gender.Male;
    }
};
// DEBUG TEST STATE:
const DEBUG_STATE = {
    debugOverride: true,
    testCompleted: true,
    navRequested: () => "",
    region: () => GetBoroughByName("Devon"),
    score: (steps) => 20,
    calories: () => 10,
    units: () => 10,
    gender: () => Gender.Female
};
// const DEBUG_STATE = PROD_STATE;
export const TestState = window.location.href.indexOf("localhost") !== -1 ? DEBUG_STATE : PROD_STATE;
export const TEST_DATA = {
    state: TestState,
    questions: [
        Question1,
        {
            question: "How often do you have a drink containing alcohol?",
            highlightInvalid: false,
            inputs: [
                {
                    kind: QuestionType.Multichoice,
                    required: true,
                    selected: null,
                    options: [
                        {
                            label: "Never",
                            score: 0
                        },
                        {
                            label: "Monthly or less",
                            score: 1
                        },
                        {
                            label: "2-4 times per month",
                            score: 2
                        },
                        {
                            label: "2-3 times per week",
                            score: 3
                        },
                        {
                            label: "4+ times per week",
                            score: 4
                        }
                    ]
                }
            ]
        },
        UnitCalculatorQuestion,
        {
            question: "How often have you had <strong>${x} or more units on a " +
                "single occasion</strong> in the last year?",
            highlightInvalid: false,
            questionTip: '<span style="color: #B82038">Remember...</span> when answering the ' +
                'previous question you worked out that on a <span style="color: #B82038">typical ' +
                "drinking day you drink ${y} units.</span>",
            dependencies: {
                x: () => ({ label: GetSelectedGender() === "Male" ? "8" : "6" }),
                y: () => UnitCalculatorQuestion.inputs[0].selected
            },
            inputs: [
                {
                    kind: QuestionType.Multichoice,
                    selected: null,
                    required: true,
                    options: [
                        {
                            label: "Never",
                            score: 0
                        },
                        {
                            label: "Less than monthly",
                            score: 1
                        },
                        {
                            label: "Monthly",
                            score: 2
                        },
                        {
                            label: "Weekly",
                            score: 3
                        },
                        {
                            label: "Daily or almost daily",
                            score: 4
                        }
                    ]
                }
            ]
        },
        {
            question: "<em>How often during the last year...</em> have you found that " +
                "you were not able to stop drinking once you had started?",
            highlightInvalid: false,
            inputs: [
                {
                    kind: QuestionType.Multichoice,
                    selected: null,
                    required: true,
                    options: [
                        {
                            label: "Never",
                            score: 0
                        },
                        {
                            label: "Less than monthly",
                            score: 1
                        },
                        {
                            label: "Monthly",
                            score: 2
                        },
                        {
                            label: "Weekly",
                            score: 3
                        },
                        {
                            label: "Daily or almost daily",
                            score: 4
                        }
                    ]
                }
            ]
        },
        {
            question: "<em>How often during the last year...</em> have you failed " +
                "to do what was normally expected from you because of your drinking?",
            highlightInvalid: false,
            inputs: [
                {
                    kind: QuestionType.Multichoice,
                    selected: null,
                    required: true,
                    options: [
                        {
                            label: "Never",
                            score: 0
                        },
                        {
                            label: "Less than monthly",
                            score: 1
                        },
                        {
                            label: "Monthly",
                            score: 2
                        },
                        {
                            label: "Weekly",
                            score: 3
                        },
                        {
                            label: "Daily or almost daily",
                            score: 4
                        }
                    ]
                }
            ]
        },
        {
            question: "<em>How often during the last year...</em> have you " +
                "needed an alcoholic drink in the morning to get yourself going " +
                "after a heavy drinking session?",
            highlightInvalid: false,
            questionTip: "When we say <strong>&ldquo;in the morning,&rdquo;</strong> we mean within five " +
                "hours of waking up. So if you wake at 3pm in the afternoon and drink before " +
                "8pm, you should count that as a &ldquo;morning&rdquo; drink.",
            inputs: [
                {
                    kind: QuestionType.Multichoice,
                    selected: null,
                    required: true,
                    options: [
                        {
                            label: "Never",
                            score: 0
                        },
                        {
                            label: "Less than monthly",
                            score: 1
                        },
                        {
                            label: "Monthly",
                            score: 2
                        },
                        {
                            label: "Weekly",
                            score: 3
                        },
                        {
                            label: "Daily or almost daily",
                            score: 4
                        }
                    ]
                }
            ]
        },
        {
            question: "<em>How often during the last year...</em> have you " +
                "had a feeling of guilt or remorse after drinking?",
            highlightInvalid: false,
            inputs: [
                {
                    kind: QuestionType.Multichoice,
                    selected: null,
                    required: true,
                    options: [
                        {
                            label: "Never",
                            score: 0
                        },
                        {
                            label: "Less than monthly",
                            score: 1
                        },
                        {
                            label: "Monthly",
                            score: 2
                        },
                        {
                            label: "Weekly",
                            score: 3
                        },
                        {
                            label: "Daily or almost daily",
                            score: 4
                        }
                    ]
                }
            ]
        },
        {
            question: "<em>How often during the last year...</em> have you " +
                "been unable to remember what happened the night before because you " +
                "had been drinking?",
            highlightInvalid: false,
            inputs: [
                {
                    kind: QuestionType.Multichoice,
                    selected: null,
                    required: true,
                    options: [
                        {
                            label: "Never",
                            score: 0
                        },
                        {
                            label: "Less than monthly",
                            score: 1
                        },
                        {
                            label: "Monthly",
                            score: 2
                        },
                        {
                            label: "Weekly",
                            score: 3
                        },
                        {
                            label: "Daily or almost daily",
                            score: 4
                        }
                    ]
                }
            ]
        },
        {
            question: "Have you or somebody else been injured as a result of your drinking?",
            highlightInvalid: false,
            inputs: [
                {
                    kind: QuestionType.Multichoice,
                    selected: null,
                    required: true,
                    options: [
                        {
                            label: "No",
                            score: 0
                        },
                        {
                            label: "Yes, but not in the last year",
                            score: 2
                        },
                        {
                            label: "Yes, during the last year",
                            score: 4
                        }
                    ]
                }
            ]
        },
        {
            question: "Has a relative or friend, doctor or other health worker been " +
                "concerned about your drinking or suggested that you cut down?",
            highlightInvalid: false,
            inputs: [
                {
                    kind: QuestionType.Multichoice,
                    selected: null,
                    required: true,
                    options: [
                        {
                            label: "No",
                            score: 0
                        },
                        {
                            label: "Yes, but not in the last year",
                            score: 2
                        },
                        {
                            label: "Yes, during the last year",
                            score: 4
                        }
                    ]
                }
            ]
        },
        {
            question: "Tell us a bit about yourself, so we can <strong>provide you with " +
                "tailored results and advice.</strong>",
            highlightInvalid: false,
            inputs: [
                {
                    kind: QuestionType.Multichoice,
                    selected: null,
                    required: true,
                    label: "Age",
                    userEvent: UserInteractionEvent.SelectAge,
                    layoutStyle: LayoutStyle.Grid,
                    columnGrouping: ColumnGrouping.Left,
                    options: [
                        {
                            label: "16-24"
                        },
                        {
                            label: "25-34"
                        },
                        {
                            label: "35-44"
                        },
                        {
                            label: "45-54"
                        },
                        {
                            label: "55-64"
                        },
                        {
                            label: "65-74"
                        },
                        {
                            label: "75-84"
                        },
                        {
                            label: "85+"
                        }
                    ]
                },
                {
                    kind: QuestionType.Select,
                    selected: null,
                    required: true,
                    label: "Select ethnicity",
                    userEvent: UserInteractionEvent.SelectEthnicity,
                    layoutStyle: LayoutStyle.SingleColumn,
                    columnGrouping: ColumnGrouping.Right,
                    options: [
                        {
                            label: "White British"
                        },
                        {
                            label: "White Irish"
                        },
                        {
                            label: "Other White"
                        },
                        {
                            label: "Mixed &ndash; White &amp; Black Caribbean"
                        },
                        {
                            label: "Mixed &ndash; White &amp; Asian"
                        },
                        {
                            label: "Other Mixed"
                        },
                        {
                            label: "Asian &ndash; British"
                        },
                        {
                            label: "Asian &ndash; Indian"
                        },
                        {
                            label: "Asian &ndash; Pakistani"
                        },
                        {
                            label: "Asian &ndash; Bangladeshi"
                        },
                        {
                            label: "Chinese"
                        },
                        {
                            label: "Other Asian"
                        },
                        {
                            label: "Black British"
                        },
                        {
                            label: "Black Caribbean"
                        },
                        {
                            label: "Black African"
                        },
                        {
                            label: "Black Other"
                        },
                        {
                            label: "Prefer Not Say"
                        },
                        {
                            label: "Other"
                        }
                    ]
                },
                {
                    kind: QuestionType.Select,
                    selected: null,
                    required: true,
                    userEvent: UserInteractionEvent.SelectSubRegion,
                    label: () => BoroughTerminology(),
                    layoutStyle: LayoutStyle.SingleColumn,
                    questionTip: () => {
                        const borough = GetBoroughByName(GetSelectedBorough());
                        if (borough.name === "Suffolk") {
                            return "<span><a style='color: rgb(0, 132, 160);margin-top: 5px;text-align: right;display: inline-block;' href='https://www.suffolkobservatory.info/suffolk-boundaries-map/' target='_blank'>" +
                                "Not sure which district?</a></span>";
                        }
                        return "";
                    },
                    columnGrouping: ColumnGrouping.Right,
                    options: GetRegionsForCurrentBorough
                },
                {
                    kind: QuestionType.Select,
                    selected: null,
                    required: true,
                    userEvent: UserInteractionEvent.SelectSexuality,
                    label: "Which of the following best describes how you think of yourself?",
                    layoutStyle: LayoutStyle.SingleColumn,
                    columnGrouping: ColumnGrouping.Right,
                    options: GetSexualityOptions
                },
                {
                    kind: QuestionType.Select,
                    selected: null,
                    required: false,
                    label: "Where did you hear about DrinkCoach?",
                    userEvent: UserInteractionEvent.WhereHeardAboutDBIU,
                    layoutStyle: LayoutStyle.SingleColumn,
                    columnGrouping: ColumnGrouping.Right,
                    options: GetReferralOptionsForRegion
                },
                {
                    kind: QuestionType.AgreeToTerms,
                    selected: null,
                    required: "Yes",
                    label: "Agree to terms",
                    columnGrouping: ColumnGrouping.Right,
                    options: [
                        {
                            label: "Yes"
                        },
                        {
                            label: "No"
                        }
                    ]
                }
            ]
        }
    ]
};
if (isNorthEastAndNorthCumbria()) {
    TEST_DATA.questions[TEST_DATA.questions.length - 1].inputs.splice(3, 0, {
        kind: QuestionType.Select,
        selected: null,
        required: true,
        userEvent: UserInteractionEvent.SelectOrgType,
        label: "Which best describes the type of organisation you work for?",
        layoutStyle: LayoutStyle.SingleColumn,
        columnGrouping: ColumnGrouping.Right,
        options: [
            "Acute NHS Trust",
            "Mental Health NHS Trust",
            "Ambulance Service",
            "Community Service",
            "Social Care organisation",
            "Other"
        ].map(option => ({ label: option })),
    }, {
        kind: QuestionType.Select,
        selected: null,
        required: true,
        userEvent: UserInteractionEvent.SelectStaffGroup,
        label: "What best describes your staff group?",
        layoutStyle: LayoutStyle.SingleColumn,
        columnGrouping: ColumnGrouping.Right,
        options: [
            "Doctor",
            "Nurse or Midwife",
            "Ambulance crew",
            "Healthcare assistant",
            "Dental health practitioner",
            "Pharmacy practitioner",
            "Allied health professional",
            "Manager/commissioner",
            "Admin/clerical",
            "Social worker",
            "Carer",
            "Estates",
            "Other",
        ].map(option => ({ label: option })),
    });
}
export const resetState = () => {
    TEST_DATA.state.testCompleted = false;
    TEST_DATA.state.debugOverride = false;
    TEST_DATA.questions.forEach((question, index) => {
        question.highlightInvalid = false;
        question.inputs.forEach(input => {
            input.selected = null;
            if (input.kind === QuestionType.UnitCalculator) {
                const drinks = input.options[0];
                drinks.selectedDrinks = [];
                drinks.score = 0;
                drinks.allDrinks.selected = null;
                drinks.currentServings.selected = null;
            }
        });
    });
};
//# sourceMappingURL=Model.js.map